<template>
  <div>
    <h2 class="mb-6">{{$t('Inventory')}}</h2>
    <div class="xl:w-1/2 lg:w-3/4 w-full ml-auto">
      <div class="inventory-card text-center w-full">
        <div class="flex items-center justify-between gap-4">
          <div>
            <p class="font-medium text-darkblue">{{ $t('Total products') }}</p>
            <p class="text-darkblue mt-1">{{ totalRows }}</p>
          </div>
          <div>
            <p class="font-medium text-darkblue">{{ $t('Total Current Inventory') }}</p>
            <p class="text-darkblue mt-1">{{ totalInventoryLevel }}</p>
          </div>
          <div>
            <p class="font-medium text-darkblue">{{ $t('Total Reserved Inventory') }}</p>
            <p class="text-darkblue mt-1">{{ totalReservedInventory }}</p>
          </div>
        </div>
      </div>
    </div>
    <print-label-button class="mr-4 mt-6 float-left whitespace-nowrap" :selected="selected"></print-label-button>
    <shipblu-table
     :sst="true"
      multiple
      v-model="selected"
      pagination
      :max-items="maximumItems"
      @search="handleSearch"
      @keyup.enter.native="handleSearch"
      search
      :data="products"
      :tableLoader="tableLoader"
    >
      <template slot="thead">
        <shipblu-th>{{$t('Product Image')}}</shipblu-th>
        <shipblu-th>{{$t('Merchant Name')}}</shipblu-th>
        <shipblu-th>{{$t('SBSIN')}}</shipblu-th>
        <shipblu-th>{{$t('Product Name')}}</shipblu-th>
        <shipblu-th>{{$t('SKU')}}</shipblu-th>
        <shipblu-th>{{$t('Description')}}</shipblu-th>
        <shipblu-th>{{$t('Size')}}</shipblu-th>
        <shipblu-th>{{$t('Color')}}</shipblu-th>
        <shipblu-th>{{$t('Dimensions')}}</shipblu-th>
        <shipblu-th>{{$t('Location')}}</shipblu-th>
        <shipblu-th>{{$t('Reserved Inventory')}}</shipblu-th>
        <shipblu-th>{{$t('Current Inventory')}}</shipblu-th>
      </template>

      <template slot-scope="{ data }">
        <shipblu-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
          <shipblu-td :data="data[indextr].image">
            <img :src=" data[indextr].image " class="w-10 h-10"/>
          </shipblu-td>
          
          <shipblu-td :data="data[indextr].merchantName">
            {{ data[indextr].group.merchant.name }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].shipBluInventoryID">
            {{ data[indextr].id }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].name">
            {{ data[indextr].name }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].sku">
            {{ data[indextr].sku }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].description">
            {{ data[indextr].description }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].size">
            {{ data[indextr].size }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].color">
            <div class="color-container h-6 w-3/4">
              <div class="w-full h-full" :style="`background-color:${data[indextr].color}`"></div>
            </div>
          </shipblu-td>

          <shipblu-td :data="data[indextr].dimensions">
            {{ data[indextr].d_length }} x {{ data[indextr].d_width }} x {{ data[indextr].d_height }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].locations">
            <p class="link" @click="openLocationModal(data[indextr])">{{ data[indextr].locations.length }}</p>
          </shipblu-td>

          <shipblu-td :data="data[indextr].reserved_inventory">
            {{ data[indextr].reserved_inventory }}
          </shipblu-td>

          <shipblu-td :data="data[indextr].inventory">
            <span v-if="$store.state.AppActiveUser.userRole === 'fc-operations-supervisor'" class="link" @click="openProductModal(data[indextr])"> {{data[indextr].current_inventory_level}}</span>
            <span v-else>{{data[indextr].current_inventory_level}}</span>
          </shipblu-td>
        </shipblu-tr>
      </template>
    </shipblu-table>
    <vs-pagination class="mt-4" :total="Math.ceil(totalRows * 1.0 / maximumItems)" v-model="currentPage"></vs-pagination>
    
    <shipblu-prompt
      @close="closeModal('updateModal')"
      class="shipment-modal"
      :active.sync="productModal"
      :title="$t('Update Product')"
      :buttons-hidden="true">
      <div class="flex justify-center items-end gap-2">
        <vs-input :label-placeholder="$t('Current Inventory Level')" @keyup.enter.native="updateProduct()" class="w-1/2" v-model="product.current_inventory_level"/>
        <vs-button icon-pack="feather" icon="icon-check" color="success" @click="updateProduct()"></vs-button>   
        <vs-button icon-pack="feather" icon="icon-x" color="danger" @click="closeModal('updateModal')"></vs-button>
      </div>
    </shipblu-prompt>
    
    <shipblu-prompt
      @close="closeModal"
      class="shipment-modal"
      :active.sync="locationModal"
      :title="$t('Location')"
      :buttons-hidden="true">
      <div class="mb-4 shadow-drop rounded-lg flex justify-between items-center p-4" v-for="location in product.locations" :key="location.index">
        <span>{{location.location}}</span>
        <div>
          <span class="text-grey text-lg font-medium"> {{$t('Qty')}}{{': '}}</span>
          <span class="text-blue-100"> {{`[${location.stored_quantity}]`}}</span>
        </div>
      </div>
    </shipblu-prompt>
    <print-product-barcode :selected="selected"/>
  </div>
</template>

<script>
import common  from '@/assets/utils/common'
import {sendFulfillmentRequest} from '../../http/axios/requestHelper.js'
import ShipbluTable from '../../layouts/components/ShipBluTable.vue'
import ShipbluTr from '../../layouts/components/ShipBluTr.vue'
import ShipbluTh from '../../layouts/components/ShipBluTh.vue'
import ShipbluTd from '../../layouts/components/ShipBluTd.vue'
import ShipbluPrompt from '../../layouts/components/ShipBluPrompt.vue'
import i18nData from '../../i18n/i18nData.js'
import PrintLabelButton from '../headOfFleet/components/PrintLabelButton.vue'
import PrintProductBarcode from '../merchant/components/PrintProductBarcode.vue'

export default {
  data () {
    return {
      maximumItems: process.env.VUE_APP_MAXIMUM_ITEMS_TABLE,
      selected: [],
      productModal: false,
      locationModal: false,
      products: [],
      product: {},
      offset: 0,
      totalRows: 0,
      currentPage: (this.$route.query.page &&  Number(this.$route.query.page) > 0) ? Number(this.$route.query.page) : 1,
      searchVal: '',
      searchInProgress: false,
      searchedValue: ' ',
      totalInventoryLevel: '',
      tableLoader: false,
      totalReservedInventory: ''
    }
  },
  watch : {
    currentPage () {
      this.offset = (this.currentPage - 1) * this.maximumItems
      this.$router.push({
        query: {
          page: this.currentPage
        }
      }).catch(() => {})
      this.loadProducts()
    }
  },
  methods: {
    closeModal (modalName) {
      this.productModal = false
      this.locationModal = false
      if (modalName) {
        this.loadProducts()
      }
    },
    loadProducts () {
      this.searchInProgress = true
      this.tableLoader = true
      const query = `?offset=${this.offset}&search=${this.searchVal}&limit=${this.maximumItems}`
      sendFulfillmentRequest(true, false, this, `api/v1/fc/products/${query}`, 'get', null, true,
        (response) => {
          this.products = response.data.results
          this.totalRows = response.data.count
          this.totalInventoryLevel = response.data.total_inventory_level
          this.totalReservedInventory = response.data.total_reserved_inventory
          this.searchedValue = this.searchVal
          this.tableLoader = false
        }
      )
      this.searchInProgress = false
    },
    openProductModal (data) {
      this.product = data
      this.productModal = true
    },
    openLocationModal (data) {
      this.product = data
      this.locationModal = true
    },
    handleSearch (search) {
      this.offset = 0
      this.currentPage = 1
      this.searchVal = search.target ? search.target.value : search
      return common.manageSearch(search, this.loadProductCatalogSearch)
    },
    loadProductCatalogSearch () {
      if (this.searchInProgress || this.searchedValue === this.searchVal) {
        return
      }
      this.loadProducts()
    },
    updateProduct () {
      sendFulfillmentRequest(false, false, this, `api/v1/fc/products/${this.product.id}/`, 'patch', {current_inventory_level: this.product.current_inventory_level}, true,
        () => {
          this.closeModal('updateModal')
          this.$vs.notify({
            color:'success',
            title:i18nData[this.$i18n.locale]['Success'],
            text:i18nData[this.$i18n.locale]['Product'] + i18nData[this.$i18n.locale][' has been updated successfully!'],
            position: 'top-center'
          })
        }
      )
    },
    printLabel (product) {
      this.printProduct = product
      window.print()
    }
  },
  components: {
    ShipbluTable,
    ShipbluTr,
    ShipbluTh,
    ShipbluTd,
    ShipbluPrompt,
    PrintLabelButton,
    PrintProductBarcode
  },
  created () {
    this.offset = (this.currentPage - 1) * this.maximumItems
    this.loadProducts()
  },
  mounted () {
    this.$emit('setAppClasses', 'shipment-page')
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
#order-label-print{
  display: none;
  visibility: hidden;
}
.con-pagination-table{
  display: none;
}
.color-container {
  border: 1px solid #000;
  border-radius: 7px;
  padding: 2px;
  margin: auto !important;
  div {
    border-radius: 5px;
  }
}
.inventory-card {
  background: #fff;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0px 4px 25px 0px rgba(0,0,0,.1);
  -webkit-transition: all .3s ease-in-out;
}
</style>